import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Autocomplete, CircularProgress, FormControlLabel, Switch } from '@mui/material';
import api from '../../api';

const CreateChannelModal = ({ open, onClose, onCreate, currentEmpId, isAdmin }) => {
  const [name, setName] = useState('');
  const [isGlobal, setIsGlobal] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  
  const [searchOptions, setSearchOptions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (searchQuery.length < 2 || isGlobal) {
      setSearchOptions([]);
      return;
    }
    const delay = setTimeout(() => {
      setIsSearching(true);
      api.get(`/chat/search-employees?q=${searchQuery}&current_emp_id=${currentEmpId}`)
        .then(res => setSearchOptions(res.data))
        .catch(err => console.error(err))
        .finally(() => setIsSearching(false));
    }, 400);

    return () => clearTimeout(delay);
  }, [searchQuery, currentEmpId, isGlobal]);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      const payload = {
        name: name.trim(),
        type: isGlobal ? 'GLOBAL' : 'GROUP',
        created_by: currentEmpId,
        members: selectedMembers.map(m => m.emp_id)
      };
      
      const res = await api.post('/chat/create-channel', payload);
      onCreate(res.data);
      handleReset();
    } catch (err) {
      console.error("Failed to create channel", err);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setName('');
    setIsGlobal(false);
    setSelectedMembers([]);
    setSearchQuery('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleReset} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800, color: '#10394f' }}>Create New Channel</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
        
        <TextField 
          label="Channel Name" 
          fullWidth 
          value={name} 
          onChange={(e) => setName(e.target.value)} 
          required 
        />

        {isAdmin && (
          <FormControlLabel
            control={<Switch checked={isGlobal} onChange={(e) => setIsGlobal(e.target.checked)} color="secondary" />}
            label="Make this a Global Channel (Adds all employees automatically)"
          />
        )}

        {!isGlobal && (
          <Autocomplete
            multiple
            options={searchOptions}
            getOptionLabel={(option) => `${option.full_name} (${option.emp_id})`}
            value={selectedMembers}
            onChange={(event, newValue) => setSelectedMembers(newValue)}
            onInputChange={(event, newInputValue) => setSearchQuery(newInputValue)}
            loading={isSearching}
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add Members"
                placeholder="Search colleagues..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={handleReset} color="inherit">Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || !name.trim() || (!isGlobal && selectedMembers.length === 0)}>
          {loading ? 'Creating...' : 'Create Channel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateChannelModal;